import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Dashboard as PerformanceIcon,
  TrendingUp as KPIIcon
} from '@mui/icons-material';

import { useTranslation } from 'react-i18next';

import useRoadmapStore from '../store/roadmapStore';
import { STEP_LABELS } from '../utils/constants';
import PageContainer from '../components/PageContainer';
import StepPageHeader from '../components/StepPageHeader';
import useAISuggest from '../hooks/useAISuggest';

const KPICard = ({ kpi, index, onEdit, onDelete }) => {
  const { t } = useTranslation();
  
  const getStatusColor = () => {
    if (!kpi.current || !kpi.target) return 'default';
    const progress = (parseFloat(kpi.current) / parseFloat(kpi.target)) * 100;
    if (progress >= 100) return 'success';
    if (progress >= 75) return 'primary';
    if (progress >= 50) return 'warning';
    return 'error';
  };

  const getProgress = () => {
    if (!kpi.current || !kpi.target) return 0;
    return Math.min((parseFloat(kpi.current) / parseFloat(kpi.target)) * 100, 100);
  };

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
          {kpi.name || 'Untitled KPI'}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {kpi.description || 'No description provided'}
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" color={`${getStatusColor()}.main`}>
            {kpi.current || 0}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Target: {kpi.target || 'Not set'}
          </Typography>
        </Box>

        {kpi.current && kpi.target && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Progress: {getProgress().toFixed(0)}%
            </Typography>
          </Box>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          Tracking: {kpi.tracking_frequency || 'Not specified'}
        </Typography>
      </CardContent>
      
      <CardActions>
        <Button size="small" startIcon={<EditIcon />} onClick={() => onEdit(index)}>
          {t('common.edit')}
        </Button>
        <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => onDelete(index)}>
          {t('common.delete')}
        </Button>
      </CardActions>
    </Card>
  );
};

const KPIForm = ({ kpi, onSave, onCancel }) => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState({
    name: kpi?.name || '',
    description: kpi?.description || '',
    target: kpi?.target || '',
    current: kpi?.current || '',
    tracking_frequency: kpi?.tracking_frequency || 'Monthly',
    unit: kpi?.unit || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.name.trim()) {
      onSave(formData);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {kpi ? t('stepContent.performance.form.editKPITitle') : t('stepContent.performance.form.addKPITitle')}
      </Typography>
      
      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <TextField
              fullWidth
              label={t('stepContent.performance.form.fields.name')}
              value={formData.name}
              onChange={handleChange('name')}
              placeholder={t('stepContent.performance.form.placeholders.name')}
              required
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label={t('stepContent.performance.form.fields.unit')}
              value={formData.unit}
              onChange={handleChange('unit')}
              placeholder={t('stepContent.performance.form.fields.unit')}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t('stepContent.performance.form.fields.description')}
              multiline
              rows={2}
              value={formData.description}
              onChange={handleChange('description')}
              placeholder={t('stepContent.performance.form.placeholders.description')}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label={t('stepContent.performance.form.fields.target')}
              type="number"
              value={formData.target}
              onChange={handleChange('target')}
              placeholder={t('stepContent.performance.form.placeholders.target')}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label={t('stepContent.performance.form.fields.current')}
              type="number"
              value={formData.current}
              onChange={handleChange('current')}
              placeholder={t('stepContent.performance.form.fields.current')}
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>{t('stepContent.performance.form.fields.frequency')}</InputLabel>
              <Select
                value={formData.tracking_frequency}
                onChange={handleChange('tracking_frequency')}
                label={t('stepContent.performance.form.fields.frequency')}
              >
                <MenuItem value="Daily">Daily</MenuItem>
                <MenuItem value="Weekly">Weekly</MenuItem>
                <MenuItem value="Monthly">Monthly</MenuItem>
                <MenuItem value="Quarterly">Quarterly</MenuItem>
                <MenuItem value="Annually">Annually</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}
              sx={{ '& > .MuiButton-root': { width: { xs: '100%', sm: 'auto' } } }}>
              <Button
                type="submit"
                variant="contained"
                disabled={!formData.name.trim()}
              >
                {kpi ? t('common.update') : t('stepContent.performance.actions.addKPI')}
              </Button>
              <Button onClick={onCancel}>
                {t('common.cancel')}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default function PerformancePage() {
  const { 
    getStepData, 
    updateStepData, 
    markStepComplete, 
    isStepCompleted 
  } = useRoadmapStore();
  const { t } = useTranslation();
  const handleAISuggest = useAISuggest('performance');
  const _storeStep = useRoadmapStore(s => s.roadmapData?.steps?.performance);

  const stepData = getStepData('performance');
  const [kpis, setKpis] = useState(stepData.kpis || []);
  const [reviewSchedule, setReviewSchedule] = useState(stepData.review_schedule || '');
  const [isComplete, setIsComplete] = useState(isStepCompleted('performance'));
  const [showForm, setShowForm] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);

  useEffect(() => {
    const data = getStepData('performance');
    setKpis(data.kpis || []);
    setReviewSchedule(data.review_schedule || '');
    setIsComplete(isStepCompleted('performance'));
  }, [getStepData, isStepCompleted, _storeStep]);

  const updateData = (newKpis, newReviewSchedule = reviewSchedule) => {
    setKpis(newKpis);
    updateStepData('performance', {
      kpis: newKpis,
      review_schedule: newReviewSchedule
    });
  };

  const handleSaveKPI = (kpiData) => {
    if (editingIndex !== null) {
      const updated = kpis.map((kpi, index) => 
        index === editingIndex ? kpiData : kpi
      );
      updateData(updated);
    } else {
      updateData([...kpis, kpiData]);
    }
    
    setShowForm(false);
    setEditingIndex(null);
  };

  const handleEditKPI = (index) => {
    setEditingIndex(index);
    setShowForm(true);
  };

  const handleDeleteKPI = (index) => {
    if (window.confirm(t('common.confirmDelete'))) {
      const updated = kpis.filter((_, i) => i !== index);
      updateData(updated);
    }
  };

  const handleSaveReviewSchedule = () => {
    updateData(kpis, reviewSchedule);
  };

  const _saveRef = useRef(null);
  useEffect(() => { _saveRef.current = handleSaveReviewSchedule; });
  useEffect(() => () => { if (_saveRef.current) _saveRef.current(); }, []); // eslint-disable-line

  const handleCompleteToggle = () => {
    const newCompleteState = !isComplete;
    const success = markStepComplete('performance', newCompleteState);
    setIsComplete(success);
  };

  const canComplete = kpis.length > 0;

  return (
    <PageContainer maxWidth={1200}>
      <StepPageHeader
        stepNum={9}
        title={t(STEP_LABELS.performance)}
        intro={t('stepContent.performance.intro')}
        isComplete={isComplete}
        canComplete={canComplete}
        onToggleComplete={handleCompleteToggle}
        requireMessage={t('stepContent.performance.alerts.requireKPIs')}
      onAISuggest={handleAISuggest}
        />
      <Paper elevation={2} sx={{ p: 4, mb: 3 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingIndex(null);
            setShowForm(true);
          }}
          sx={{ mb: 3 }}
        >
          {t('stepContent.performance.actions.addKPI')}
        </Button>
      </Paper>

      {/* KPI Form */}
      {showForm && (
        <Box sx={{ mb: 3 }}>
          <KPIForm
            kpi={editingIndex !== null ? kpis[editingIndex] : null}
            onSave={handleSaveKPI}
            onCancel={() => {
              setShowForm(false);
              setEditingIndex(null);
            }}
          />
        </Box>
      )}

      {/* KPIs List */}
      {kpis.length > 0 && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <KPIIcon />
            {t('stepContent.performance.sections.kpis.title')} ({kpis.length})
          </Typography>
          
          <Grid container spacing={3}>
            {kpis.map((kpi, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <KPICard
                  kpi={kpi}
                  index={index}
                  onEdit={handleEditKPI}
                  onDelete={handleDeleteKPI}
                />
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Review Schedule */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PerformanceIcon />
          {t('stepContent.performance.sections.dashboards.title')}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('stepContent.performance.sections.dashboards.description')}
        </Typography>

        <TextField
          fullWidth
          multiline
          rows={6}
          label={t('stepContent.performance.form.fields.description')}
          value={reviewSchedule}
          onChange={(e) => setReviewSchedule(e.target.value)}
          onBlur={handleSaveReviewSchedule}
          placeholder={t('stepContent.performance.form.fields.description')}
          sx={{ mb: 3 }}
        />

        <Button
          variant="contained"
          onClick={handleSaveReviewSchedule}
        >
          {t('common.save')}
        </Button>
      </Paper>

      {kpis.length === 0 && !showForm && (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('stepContent.performance.empty.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('stepContent.performance.empty.description')}
          </Typography>
        </Paper>
      )}
    </PageContainer>
  );
}
